const RTC_CONFIG = {
    iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "turn:live.opendaw.studio:3478", username: "opendaw", credential: "opendaw" }
    ]
};
const BUFFERED_AMOUNT_HIGH = 1_048_576;
const BUFFERED_AMOUNT_LOW = 262_144;
export class AssetPeerConnection {
    #connection;
    #signaling;
    #localPeerId;
    #remotePeerId;
    #pendingCandidates = [];
    #remoteDescriptionSet = false;
    #channel = null;
    #terminated = false;
    constructor(signaling, localPeerId, remotePeerId) {
        this.#signaling = signaling;
        this.#localPeerId = localPeerId;
        this.#remotePeerId = remotePeerId;
        this.#connection = new RTCPeerConnection(RTC_CONFIG);
        this.#connection.oniceconnectionstatechange = () => {
            console.debug("[P2P:RTC]", localPeerId, "→", remotePeerId, "ICE:", this.#connection.iceConnectionState);
        };
        this.#connection.onconnectionstatechange = () => {
            console.debug("[P2P:RTC]", localPeerId, "→", remotePeerId, "connection:", this.#connection.connectionState);
        };
        this.#connection.onicecandidate = (event) => {
            if (event.candidate !== null) {
                this.#signaling.publish({
                    type: "rtc-ice-candidate",
                    peerId: this.#localPeerId,
                    targetPeerId: this.#remotePeerId,
                    candidate: event.candidate.toJSON()
                });
            }
        };
    }
    get channel() { return this.#channel; }
    async createOffer() {
        const channel = this.#connection.createDataChannel("assets", { ordered: true });
        channel.binaryType = "arraybuffer";
        this.#channel = channel;
        const offer = await this.#connection.createOffer();
        await this.#connection.setLocalDescription(offer);
        this.#signaling.publish({
            type: "rtc-offer",
            peerId: this.#localPeerId,
            targetPeerId: this.#remotePeerId,
            sdp: offer.sdp
        });
        return channel;
    }
    async handleOffer(sdp) {
        const { promise, resolve } = Promise.withResolvers();
        this.#connection.ondatachannel = (event) => {
            event.channel.binaryType = "arraybuffer";
            this.#channel = event.channel;
            resolve(event.channel);
        };
        await this.#connection.setRemoteDescription({ type: "offer", sdp });
        this.#remoteDescriptionSet = true;
        await this.#drainPendingCandidates();
        const answer = await this.#connection.createAnswer();
        await this.#connection.setLocalDescription(answer);
        this.#signaling.publish({
            type: "rtc-answer",
            peerId: this.#localPeerId,
            targetPeerId: this.#remotePeerId,
            sdp: answer.sdp
        });
        return promise;
    }
    async handleAnswer(sdp) {
        await this.#connection.setRemoteDescription({ type: "answer", sdp });
        this.#remoteDescriptionSet = true;
        await this.#drainPendingCandidates();
    }
    async handleIceCandidate(candidate) {
        if (this.#remoteDescriptionSet) {
            await this.#connection.addIceCandidate(candidate);
        }
        else {
            this.#pendingCandidates.push(candidate);
        }
    }
    async sendWithBackpressure(channel, data) {
        if (channel.bufferedAmount > BUFFERED_AMOUNT_HIGH) {
            const { promise, resolve } = Promise.withResolvers();
            channel.bufferedAmountLowThreshold = BUFFERED_AMOUNT_LOW;
            channel.onbufferedamountlow = () => {
                channel.onbufferedamountlow = null;
                resolve();
            };
            await promise;
        }
        channel.send(data);
    }
    async #drainPendingCandidates() {
        for (const candidate of this.#pendingCandidates) {
            await this.#connection.addIceCandidate(candidate);
        }
        this.#pendingCandidates.length = 0;
    }
    terminate() {
        if (this.#terminated) {
            return;
        }
        this.#terminated = true;
        if (this.#channel !== null) {
            this.#channel.close();
        }
        this.#connection.close();
    }
}
